import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GitCompareArrows, Info, Scale, Wallet } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate, formatRelative } from '@/lib/formatters';
import {
  useBankReconciliation,
  useUnmappedQboBankAccounts,
  type BankReconciliationRow,
  type UnmappedQboBankAccountRow,
} from '@/features/financials/hooks';

/**
 * Reconciliation — Mercury / Plaid live balances next to the QuickBooks
 * register balance for the same account. The view is explicit about the
 * fact that the two sides have different time semantics: Mercury &
 * Plaid are real-time (current_balance_cents on bank_accounts), while
 * the QBO column is the end-of-most-recent-complete-month figure from
 * the QBO BalanceSheet report (via v_balance_sheet / qbo_balance_monthly).
 *
 * For that reason the page never claims a difference is "wrong" — it
 * highlights non-zero diffs and surfaces the per-account count of
 * Atlas transactions that haven't been pushed to QBO yet as the most
 * likely explanation. Once the operator zeros out the unposted queue
 * AND the books are closed in QBO, the diff should collapse to zero
 * for any given mapped account.
 *
 * Anything genuinely actionable goes in one of two side panels:
 *   - "What's different" — mapped accounts where |Δ| > $0.01, sorted
 *     by absolute difference so the biggest reconciliation gap is at
 *     the top.
 *   - "Coverage gaps" — Mercury accounts with no QBO mapping (operator
 *     hasn't picked the destination register yet) and QBO bank/credit
 *     accounts with no Mercury/Plaid feed (Atlas has no live data for
 *     them at all). Both link the operator to the Accounts page where
 *     mappings live.
 */
export function ReconciliationPage() {
  const { data: rows, isLoading } = useBankReconciliation();
  const { data: unmappedQbo, isLoading: unmappedQboLoading } =
    useUnmappedQboBankAccounts();

  const accounts = rows ?? [];
  const mapped = accounts.filter((r) => r.qb_account_id != null);
  const unmappedMercury = accounts.filter((r) => r.qb_account_id == null);

  // KPIs roll up the deposit-side mapped accounts. Credit accounts are
  // excluded from the totals because cash-on-hand vs amount-owed live
  // on opposite sides of the balance sheet — summing them together
  // would produce a meaningless figure. The per-account table below
  // still shows credit accounts row-by-row.
  const deposits = mapped.filter((r) => !r.is_credit);
  const credits = mapped.filter((r) => r.is_credit);

  const totals = useMemo(() => {
    const sumLive = (set: BankReconciliationRow[]) =>
      set.reduce((acc, r) => acc + r.current_balance_cents, 0);
    const sumQbo = (set: BankReconciliationRow[]) =>
      set.reduce((acc, r) => acc + (r.qbo_balance_cents ?? 0), 0);
    return {
      depositLive: sumLive(deposits),
      depositQbo: sumQbo(deposits),
      depositDiff: sumLive(deposits) - sumQbo(deposits),
      creditLive: sumLive(credits),
      creditQbo: sumQbo(credits),
    };
  }, [deposits, credits]);

  const differences = useMemo(
    () =>
      mapped
        .filter(
          (r) => r.difference_cents != null && Math.abs(r.difference_cents) > 0,
        )
        .sort(
          (a, b) =>
            Math.abs(b.difference_cents ?? 0) - Math.abs(a.difference_cents ?? 0),
        ),
    [mapped],
  );

  // The "as-of" footer uses the first row's qbo_as_of — the view picks
  // a single org-wide max(month) so every row carries the same date.
  // We also surface the freshest Mercury sync timestamp as a proxy for
  // "how stale is the live side?".
  const qboAsOf = mapped.find((r) => r.qbo_as_of)?.qbo_as_of ?? null;
  const mostRecentSync = mapped.reduce<string | null>((acc, r) => {
    if (!r.last_synced_at) return acc;
    if (!acc) return r.last_synced_at;
    return r.last_synced_at > acc ? r.last_synced_at : acc;
  }, null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reconciliation"
      />

      <AsOfStrip qboAsOf={qboAsOf} mostRecentSync={mostRecentSync} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          title="Live cash on hand"
          value={totals.depositLive}
          subtitle={`${deposits.length} mapped deposit ${deposits.length === 1 ? 'account' : 'accounts'}`}
          loading={isLoading}
        />
        <SummaryCard
          title="QuickBooks snapshot"
          value={totals.depositQbo}
          subtitle={qboAsOf ? `As of ${formatDate(qboAsOf)}` : 'No QBO data yet'}
          loading={isLoading}
        />
        <SummaryCard
          title="Unexplained difference"
          value={totals.depositDiff}
          subtitle="Live cash − QBO snapshot"
          loading={isLoading}
          tone={
            Math.abs(totals.depositDiff) < 1
              ? 'neutral'
              : Math.abs(totals.depositDiff) < 100_00
                ? 'subtle'
                : 'warning'
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account-by-account</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : mapped.length === 0 ? (
            <EmptyState
              icon={GitCompareArrows}
              title="Nothing to reconcile yet"
              action={
                <Link
                  to="/financials/bank-accounts"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-old-growth hover:underline"
                >
                  Open Accounts <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              }
            />
          ) : (
            <>
              <ReconciliationTable rows={deposits} kind="deposit" />
              {credits.length > 0 && (
                <div className="mt-6">
                  <ReconciliationTable rows={credits} kind="credit" />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <DifferencesPanel rows={differences} loading={isLoading} />

      <CoverageGapsPanel
        mercuryUnmapped={unmappedMercury}
        qboUnmapped={unmappedQbo ?? []}
        loading={isLoading || unmappedQboLoading}
      />
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function AsOfStrip({
  qboAsOf,
  mostRecentSync,
}: {
  qboAsOf: string | null;
  mostRecentSync: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-border bg-card/60 px-4 py-2 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Wallet className="h-3.5 w-3.5" strokeWidth={1.8} />
        Mercury / Plaid:{' '}
        <span className="text-foreground">
          {mostRecentSync ? formatRelative(mostRecentSync) : 'never synced'}
        </span>
      </span>
      <span className="flex items-center gap-1.5">
        <Scale className="h-3.5 w-3.5" strokeWidth={1.8} />
        QuickBooks snapshot:{' '}
        <span className="text-foreground">
          {qboAsOf ? `end of ${formatDate(qboAsOf)}` : 'no data'}
        </span>
      </span>
      <Tooltip>
        <TooltipTrigger className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground">
          <Info className="h-3.5 w-3.5" strokeWidth={1.8} />
          Why are they different?
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs leading-relaxed">
          Mercury & Plaid surface a real-time balance. QuickBooks data is
          the BalanceSheet report rolled up to the end of the most recent
          complete month. So any activity in the current month, and any
          transactions Atlas hasn&apos;t yet pushed to QBO, both show up as a
          Δ here. Both should disappear once QBO closes the period and the
          unposted queue is cleared.
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function ReconciliationTable({
  rows,
  kind,
}: {
  rows: BankReconciliationRow[];
  kind: 'deposit' | 'credit';
}) {
  if (rows.length === 0) return null;
  const heading = kind === 'deposit' ? 'Bank accounts' : 'Credit accounts';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-sm font-medium text-muted-foreground">{heading}</h3>
        {kind === 'credit' && (
          <span className="text-xs text-muted-foreground">
            Positive values = amount owed
          </span>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Account</TableHead>
            <TableHead>Posts to (QBO)</TableHead>
            <TableHead className="text-right">Live</TableHead>
            <TableHead className="text-right">QBO snapshot</TableHead>
            <TableHead className="text-right">Δ</TableHead>
            <TableHead className="text-right">Unposted</TableHead>
            <TableHead>Synced</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <ReconciliationRow key={r.bank_account_id} row={r} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ReconciliationRow({ row }: { row: BankReconciliationRow }) {
  const diff = row.difference_cents;
  const noQbo = row.qbo_balance_cents == null;
  // Treat anything ≤ 1¢ as zero so floating-point / rounding noise
  // doesn't paint perfectly-reconciled rows orange.
  const reconciled = diff != null && Math.abs(diff) < 1;
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {row.bank_account_name}
          </span>
          <span className="text-xs text-muted-foreground">
            {row.bank_institution}{' '}
            <Badge variant="muted" className="ml-1">
              {row.bank_provider}
            </Badge>
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm">
          {row.qb_account_name ? (
            <>
              {row.qb_account_code ? (
                <span className="text-muted-foreground">{row.qb_account_code} · </span>
              ) : null}
              {row.qb_account_name}
            </>
          ) : (
            <Link
              to="/financials/bank-accounts"
              className="text-fresh-salmon-dark hover:underline"
            >
              Unmapped — pick a QBO account
            </Link>
          )}
        </span>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatCurrency(row.current_balance_cents)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        {noQbo ? '—' : formatCurrency(row.qbo_balance_cents)}
      </TableCell>
      <TableCell className="text-right">
        {noQbo ? (
          <span className="text-muted-foreground">—</span>
        ) : reconciled ? (
          <Badge variant="success" className="font-normal">
            Reconciled
          </Badge>
        ) : (
          <span
            className={cn(
              'tabular-nums font-medium',
              (diff ?? 0) > 0 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark',
            )}
          >
            {(diff ?? 0) > 0 ? '+' : ''}
            {formatCurrency(diff ?? 0)}
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {row.unposted_count === 0 ? (
          <span className="text-xs text-muted-foreground">0</span>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/financials/transactions"
                className="inline-flex items-center gap-1 text-sm text-old-growth hover:underline"
              >
                <span className="tabular-nums">{row.unposted_count}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  ({formatCurrency(row.unposted_signed_cents)})
                </span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              {row.unposted_count} transaction
              {row.unposted_count === 1 ? '' : 's'} on this account haven&apos;t
              reached QBO yet. Signed total reflects how much they will
              move the {row.is_credit ? 'balance owed' : 'cash balance'}{' '}
              once posted. Open Transactions and filter by{' '}
              <span className="font-medium">{row.bank_account_name}</span> in
              the Source column to see them.
            </TooltipContent>
          </Tooltip>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {row.last_synced_at ? formatRelative(row.last_synced_at) : '—'}
      </TableCell>
    </TableRow>
  );
}

function DifferencesPanel({
  rows,
  loading,
}: {
  rows: BankReconciliationRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>What&apos;s different</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24" />
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>What&apos;s different</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">All accounts agree.</p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => {
              const diff = r.difference_cents ?? 0;
              return (
                <li
                  key={r.bank_account_id}
                  className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">
                      {r.bank_account_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Live {formatCurrency(r.current_balance_cents)} vs QBO{' '}
                      {formatCurrency(r.qbo_balance_cents)}
                      {r.unposted_count > 0 && (
                        <>
                          {' '}
                          · {r.unposted_count} unposted txn
                          {r.unposted_count === 1 ? '' : 's'}{' '}
                          ({formatCurrency(r.unposted_signed_cents)})
                        </>
                      )}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 tabular-nums font-medium',
                      diff > 0 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark',
                    )}
                  >
                    {diff > 0 ? '+' : ''}
                    {formatCurrency(diff)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CoverageGapsPanel({
  mercuryUnmapped,
  qboUnmapped,
  loading,
}: {
  mercuryUnmapped: BankReconciliationRow[];
  qboUnmapped: UnmappedQboBankAccountRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Coverage gaps</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24" />
        </CardContent>
      </Card>
    );
  }

  if (mercuryUnmapped.length === 0 && qboUnmapped.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Coverage gaps</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-medium text-muted-foreground">
              Mercury / Plaid · no QBO mapping
            </h3>
            {mercuryUnmapped.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">
                Every connected account is mapped.
              </p>
            ) : (
              <ul className="space-y-2">
                {mercuryUnmapped.map((r) => (
                  <li
                    key={r.bank_account_id}
                    className="flex items-start justify-between gap-3 rounded-md border border-border bg-card/50 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">
                        {r.bank_account_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {r.bank_institution}{' '}
                        <Badge variant="muted" className="ml-1">
                          {r.bank_provider}
                        </Badge>
                      </p>
                    </div>
                    <span className="shrink-0 tabular-nums text-sm">
                      {formatCurrency(r.current_balance_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-medium text-muted-foreground">
              QuickBooks · no live feed
            </h3>
            {qboUnmapped.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">None.</p>
            ) : (
              <ul className="space-y-2">
                {qboUnmapped.map((r) => (
                  <li
                    key={r.qb_account_id}
                    className="flex items-start justify-between gap-3 rounded-md border border-border bg-card/50 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">
                        {r.qb_account_code ? (
                          <span className="text-muted-foreground">
                            {r.qb_account_code} ·{' '}
                          </span>
                        ) : null}
                        {r.qb_account_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {r.qb_account_subtype ?? r.qb_account_type}
                      </p>
                    </div>
                    <span className="shrink-0 tabular-nums text-sm text-muted-foreground">
                      {formatCurrency(r.qbo_balance_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
  loading,
  tone = 'neutral',
}: {
  title: string;
  value: number;
  subtitle?: string;
  loading: boolean;
  tone?: 'neutral' | 'subtle' | 'warning';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <p
            className={cn(
              'text-2xl font-medium tracking-headline tabular-nums',
              tone === 'warning' && 'text-fresh-salmon-dark',
              tone === 'subtle' && 'text-foreground/80',
            )}
          >
            {value > 0 && tone !== 'neutral' ? '+' : ''}
            {formatCurrency(value)}
          </p>
        )}
        {subtitle && (
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}
